import React, { useState, useRef, useEffect } from 'react';
import {
  Sparkles, Send, RefreshCw, AlertCircle, ShieldAlert,
  Download, ChevronDown, ChevronRight, Bot, User as UserIcon,
  Table2, BarChart3,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { csvCell } from '../utils/csvSafe';
import { NavBar } from './NavBar';

// Deliberately: only BarChart and LineChart are imported above. There is no
// PieChart import anywhere in this file, and ChartSpec['type'] below is typed
// to make anything else a compile error — pie charts have no code path here.

const CHART_COLORS = ['#4f46e5', '#0891b2', '#16a34a', '#ea580c', '#be185d', '#854d0e'];

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChartSpec {
  type: 'bar' | 'line';   // no other value is representable — see note above
  x: string;
  y: string;
  series?: string | null;
  title: string;
}

interface ConversationTurn {
  question: string;
  raw_response?: string;   // needed for multi-turn context on the next request
  sql?: string;
  explanation?: string;
  data?: Record<string, string>[];
  count?: number;
  truncated?: boolean;
  chart?: ChartSpec | null;
  viewMode?: 'table' | 'chart';
  error?: string;
  isSafetyRejection?: boolean;
}

function toNum(s: string | undefined): number {
  if (!s) return 0;
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function exportCSV(data: Record<string, string>[], label: string) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(r => headers.map(h => csvCell(r[h])).join(','));
  const csv = [headers.map(csvCell).join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ai-assistant-${label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── SQL detail (collapsible) ──────────────────────────────────────────────────

function SqlDetail({ sql }: { sql: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <span>{open ? 'Hide' : 'Show'} generated SQL</span>
      </button>
      {open && (
        <pre className="mt-2 bg-gray-900 text-gray-100 text-xs p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
          {sql}
        </pre>
      )}
    </div>
  );
}

// ─── Result table ───────────────────────────────────────────────────────────────

function ResultTable({ data }: { data: Record<string, string>[] }) {
  if (!data.length) {
    return <p className="text-sm text-gray-400 mt-2">No rows returned.</p>;
  }
  const headers = Object.keys(data[0]);
  const isNumeric = (h: string) => data.every(r => r[h] === '' || r[h] == null || !isNaN(parseFloat(r[h])));

  return (
    <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {headers.map(h => (
              <th key={h} className="text-left px-3 py-2 font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                {h.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.slice(0, 200).map((row, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {headers.map(h => (
                <td key={h} className={`px-3 py-1.5 whitespace-nowrap ${isNumeric(h) ? 'text-right tabular-nums text-gray-800' : 'text-gray-700'}`}>
                  {isNumeric(h) && row[h] !== '' && row[h] != null
                    ? toNum(row[h]).toLocaleString('en-US', { maximumFractionDigits: 2 })
                    : (row[h] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length > 200 && (
        <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-200">
          Showing first 200 of {data.length} rows — export CSV for the full set.
        </div>
      )}
    </div>
  );
}

function reshapeForChart(data: Record<string, string>[], spec: ChartSpec) {
  if (!spec.series) {
    // Single series — use rows as-is, just coerce the y value to a number
    return {
      rows: data.map(r => ({ [spec.x]: r[spec.x], [spec.y]: toNum(r[spec.y]) })),
      seriesNames: [spec.y],
    };
  }
  // Grouped — pivot long-form rows into one row per x value, one column per series value
  const byX: Record<string, any> = {};
  const seriesSeen = new Set<string>();
  data.forEach(row => {
    const xVal = row[spec.x];
    if (!byX[xVal]) byX[xVal] = { [spec.x]: xVal };
    const seriesVal = row[spec.series!];
    byX[xVal][seriesVal] = toNum(row[spec.y]);
    seriesSeen.add(seriesVal);
  });
  return {
    rows: Object.values(byX),
    seriesNames: Array.from(seriesSeen).filter(Boolean),
  };
}

function AssistantChart({ data, spec }: { data: Record<string, string>[]; spec: ChartSpec }) {
  const { rows, seriesNames } = reshapeForChart(data, spec);
  if (!rows.length) return null;

  const ChartComponent = spec.type === 'line' ? LineChart : BarChart;

  return (
    <div className="mt-3 border border-gray-200 rounded-lg p-3">
      {spec.title && <p className="text-xs font-semibold text-gray-600 mb-2">{spec.title}</p>}
      <ResponsiveContainer width="100%" height={260}>
        <ChartComponent data={rows} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey={spec.x} tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(v: any) => (typeof v === 'number' ? v.toLocaleString() : v)} />
          {seriesNames.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {spec.type === 'line'
            ? seriesNames.map((name, i) => (
                <Line key={name} type="monotone" dataKey={name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
              ))
            : seriesNames.map((name, i) => (
                <Bar
                  key={name}
                  dataKey={name}
                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                  stackId={seriesNames.length > 1 ? 'a' : undefined}
                />
              ))}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AIAssistant() {
  const { getToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [input,   setInput]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversation, loading]);

  const askQuestion = async () => {
    const question = input.trim();
    if (!question || loading) return;

    setInput('');
    setError(null);
    setLoading(true);

    // history sent to the API — only the fields the Lambda actually needs
    const history = conversation
      .filter(t => t.raw_response && !t.error)
      .map(t => ({ question: t.question, raw_response: t.raw_response }));

    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/assistant`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, conversation_history: history }),
      });
      const result = await res.json();

      if (!res.ok) {
        const isSafety = res.status === 422 && (result.error || '').includes('safely');
        setConversation(prev => [...prev, {
          question,
          error: result.error ?? `HTTP ${res.status}`,
          isSafetyRejection: isSafety,
        }]);
      } else {
        setConversation(prev => [...prev, {
          question,
          raw_response: result.raw_response,
          sql: result.sql,
          explanation: result.explanation,
          data: result.data,
          count: result.count,
          truncated: result.truncated,
          chart: result.chart,
          viewMode: result.chart ? 'chart' : 'table',
        }]);
      }
    } catch (e: any) {
      setConversation(prev => [...prev, { question, error: e.message ?? 'Request failed' }]);
    } finally {
      setLoading(false);
    }
  };

  const setTurnViewMode = (index: number, mode: 'table' | 'chart') => {
    setConversation(prev => prev.map((t, i) => (i === index ? { ...t, viewMode: mode } : t)));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askQuestion();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavBar />

      <div className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col">

        <div className="flex items-center space-x-3 mb-5">
          <div className="flex items-center justify-center w-10 h-10 bg-indigo-600 rounded-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">AI Assistant</h1>
            <p className="text-sm text-gray-500">Ask a question about your data in plain English</p>
          </div>
        </div>

        {/* Conversation */}
        <div className="flex-1 space-y-4 mb-4">
          {conversation.length === 0 && !loading && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-16 px-6">
              <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-sm font-medium text-gray-500">Ask a question to get started</p>
              <p className="text-xs text-gray-400 mt-2">
                Try: "What was our total disposal tonnage last month?" or
                "Which facility received the most tons this year?"
              </p>
            </div>
          )}

          {conversation.map((turn, i) => (
            <div key={i} className="space-y-2">
              {/* User question */}
              <div className="flex items-start justify-end space-x-2">
                <div className="bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-lg text-sm">
                  {turn.question}
                </div>
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-1">
                  <UserIcon className="w-4 h-4 text-gray-500" />
                </div>
              </div>

              {/* Assistant response */}
              <div className="flex items-start space-x-2">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex-1 bg-white rounded-2xl rounded-tl-sm border border-gray-200 shadow-sm px-4 py-3 max-w-2xl">
                  {turn.error ? (
                    <div className="flex items-start space-x-2">
                      {turn.isSafetyRejection
                        ? <ShieldAlert className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        : <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                      <p className={`text-sm ${turn.isSafetyRejection ? 'text-amber-700' : 'text-red-600'}`}>
                        {turn.error}
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-800">{turn.explanation}</p>

                      {turn.chart && (
                        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 w-fit mt-2">
                          <button
                            onClick={() => setTurnViewMode(i, 'table')}
                            className={`flex items-center space-x-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${turn.viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                          >
                            <Table2 className="w-3 h-3" /><span>Table</span>
                          </button>
                          <button
                            onClick={() => setTurnViewMode(i, 'chart')}
                            className={`flex items-center space-x-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${turn.viewMode === 'chart' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                          >
                            <BarChart3 className="w-3 h-3" /><span>Chart</span>
                          </button>
                        </div>
                      )}

                      {turn.chart && turn.viewMode === 'chart' && turn.data ? (
                        <AssistantChart data={turn.data} spec={turn.chart} />
                      ) : (
                        turn.data && <ResultTable data={turn.data} />
                      )}

                      {turn.sql && <SqlDetail sql={turn.sql} />}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-gray-400">
                          {turn.count} row{turn.count !== 1 ? 's' : ''}{turn.truncated ? ' (truncated)' : ''}
                        </span>
                        {turn.data && turn.data.length > 0 && (
                          <button
                            onClick={() => exportCSV(turn.data!, turn.question)}
                            className="flex items-center space-x-1 text-xs text-gray-500 hover:text-indigo-600 transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span>Export CSV</span>
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex items-start space-x-2">
              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="bg-white rounded-2xl rounded-tl-sm border border-gray-200 shadow-sm px-4 py-3 flex items-center space-x-2">
                <RefreshCw className="w-4 h-4 text-indigo-500 animate-spin" />
                <span className="text-sm text-gray-500">Thinking…</span>
              </div>
            </div>
          )}

          <div ref={scrollRef} />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {/* Input */}
        <div className="sticky bottom-4 bg-white rounded-2xl border border-gray-200 shadow-lg p-2 flex items-end space-x-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question about your reports, tonnage, or billing…"
            rows={1}
            disabled={loading}
            className="flex-1 resize-none border-none outline-none px-3 py-2 text-sm text-gray-800 placeholder-gray-400 disabled:bg-transparent"
          />
          <button
            onClick={askQuestion}
            disabled={loading || !input.trim()}
            className="flex items-center justify-center w-9 h-9 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
